import { AnimatePresence, motion } from 'motion/react';
import React, { useId, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@column/ui';

import { BUSINESS_LEGAL_TYPE_OPTIONS } from '../_util/entityOptions';
import {
  BusinessEntityFormValues,
  IdentificationDocument,
  createDefaultAssociatedPerson,
} from '../_util/useEntityForm';
import { AddressField, DateInput, HiddenInput, RenderFields, Section, SectionHeader } from '~/components';
import { SectionWithFields } from '~/components/RenderFields';
import { useAddress } from '~/hooks';
import { Card, Flex, Grid, Inner } from '~/styles';

import { AssociatedPersonFields } from './AssociatedPersonFields';
import { PaymentRailsInput, StringListInput, YesNoInput } from './EntityFieldInputs';

// EIN field used during entity creation. Built on the same @column/ui Input as every
// other field on the form (so its resting and focus styling match exactly), with two
// trailing controls overlaid inside the input, right-aligned. They're mutually exclusive
// and cross-fade on the value: a "Pending" toggle while the field is empty (an EIN and a
// pending EIN can't coexist), and a visibility toggle once a value is entered so the EIN
// can be masked.
const EinField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  isPending: boolean;
  disabled?: boolean;
  onPendingChange: (checked: boolean) => void;
}> = ({ value, onChange, onBlur, isPending, disabled, onPendingChange }) => {
  const [isVisible, setIsVisible] = useState(true);
  const pendingId = useId();
  const hasValue = value.length > 0;

  return (
    <div className="relative flex w-full items-center">
      <Input
        type={isVisible ? 'text' : 'password'}
        autoComplete="off"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        placeholder="EIN"
        disabled={disabled || isPending}
        className={cn('w-full', hasValue ? 'pr-11' : 'pr-28')}
      />
      <div className="absolute inset-y-0 right-3 flex items-center">
        <AnimatePresence initial={false} mode="wait">
          {hasValue ? (
            <motion.div
              key="ein-visibility"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <Button
                type="button"
                variant="muted"
                size="icon-xs"
                aria-label={isVisible ? 'Hide EIN' : 'Show EIN'}
                onMouseDown={(event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                onClick={() => setIsVisible((visible) => !visible)}
              >
                {isVisible ? (
                  <Icon.EyeClosed className="[--icon-size:16px]" />
                ) : (
                  <Icon.Eye className="[--icon-size:16px]" />
                )}
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="ein-pending"
              className="flex items-center gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
            >
              <Label htmlFor={pendingId} className="cursor-pointer text-sm font-medium text-gray-500 select-none">
                Pending
              </Label>
              <Checkbox
                id={pendingId}
                checked={isPending}
                disabled={disabled}
                onCheckedChange={(checked) => onPendingChange(checked === true)}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export const BusinessEntity: React.FC<{
  id?: string;
  disableForm?: boolean;
  isRoot?: boolean;
}> = (props) => {
  const form = useFormContext();
  const { countries, states } = useAddress({ types: ['state', 'country'] });
  const [businessAddressSearchValue, setBusinessAddressSearchValue] = useState('');
  const associatedPersonRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'associatedPersons',
  });

  const requiresUSBusinessAddressRegion = form.watch('address.countryCode') === 'US';
  const isEinPending = form.watch('einPending') === true;
  // Watched (not getValues) so the State of Incorporation options refilter reactively when the
  // country changes, and so the state list is only ever built for the chosen country.
  const incorporationCountryCode = form.watch('countryOfIncorporation') as string | undefined;

  const countryOptions = countries.map((c) => ({
    label: c.name,
    value: c.code,
  }));

  const businessSections: SectionWithFields<BusinessEntityFormValues, undefined>[] = [
    {
      headline: 'Business Information',
      fields: [
        {
          id: 'businessName',
          label: 'Business Name',
          rules: { required: true },
          children: ({ value, onChange, onBlur }, { isTouched, error }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Business Name"
              disabled={props.disableForm}
              aria-invalid={isTouched && !!error ? true : undefined}
            />
          ),
        },
        {
          id: 'ein',
          label: 'Employer Identification Number',
          children: ({ value, onChange, onBlur, ref }) => {
            const showEinPending = !props.isRoot && !props.id && (!props.disableForm || isEinPending);
            const einValue = (value as string) ?? '';

            // Outside the create flow the EIN is a masked field with no "pending" concept.
            if (!showEinPending) {
              return (
                <HiddenInput
                  value={einValue}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                  placeholder="EIN"
                  defaultIsVisible={!props.id}
                  isDisabled={props.disableForm}
                  readOnly={false}
                  showCopy={false}
                  visibilityLabel="Employer Identification Number"
                />
              );
            }

            return (
              <EinField
                value={einValue}
                onChange={onChange}
                onBlur={onBlur}
                isPending={isEinPending}
                disabled={props.disableForm}
                onPendingChange={(checked) => {
                  if (props.disableForm) return;

                  form.setValue('einPending', checked, { shouldDirty: true, shouldValidate: true });
                  if (checked) {
                    form.setValue('ein', '', { shouldDirty: true, shouldValidate: true });
                  }
                }}
              />
            );
          },
        },
        {
          id: 'website',
          label: 'Website',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Website"
              disabled={props.disableForm}
            />
          ),
        },
      ],
    },
    {
      headline: 'Address',
      fields: [
        {
          id: 'address',
          label: 'Search Address',
          children: () => (
            <AddressField
              onChange={(a) => {
                form.setValue('address.line1', a.line1);
                form.setValue('address.line2', a.line2);
                form.setValue('address.city', a.city);
                form.setValue('address.state', a.state);
                form.setValue('address.postalCode', a.postalCode);
                form.setValue('address.countryCode', a.countryCode);
                setBusinessAddressSearchValue('');
              }}
              justUS
              isDisabled={props.disableForm}
              value={businessAddressSearchValue}
              onValueChange={setBusinessAddressSearchValue}
            />
          ),
        },
        {
          id: 'address.line1',
          label: 'Line 1',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Line 1"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.line2',
          label: 'Line 2',
          children: ({ value, onChange }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Line 2"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.city',
          label: 'City',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="City"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.state',
          label: 'State',
          rules: requiresUSBusinessAddressRegion ? { required: true } : undefined,
          children: ({ value, onChange }) => {
            const countryCode = form.getValues('address.countryCode');
            // Default the state options to the U.S. until a country is chosen.
            const filteredStates = states.filter((s) => s.country_code === (countryCode || 'US'));
            return (
              <Select value={(value as string) ?? ''} onValueChange={onChange} disabled={props.disableForm}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select State..." />
                </SelectTrigger>
                <SelectContent>
                  {filteredStates.map((s) => (
                    <SelectItem key={s.state_code} value={s.state_code}>
                      {s.name} ({s.state_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          },
        },
        {
          id: 'address.postalCode',
          label: 'Postal Code',
          rules: requiresUSBusinessAddressRegion ? { required: true } : undefined,
          children: ({ value, onChange }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              placeholder="Postal Code"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'address.countryCode',
          label: 'Country',
          rules: { required: true },
          children: ({ value, onChange }) => (
            <Select value={(value as string) ?? ''} onValueChange={onChange} disabled={props.disableForm}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Country..." />
              </SelectTrigger>
              <SelectContent>
                {countryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ),
        },
      ],
    },
    {
      headline: 'Business Details',
      isCollapsed: true,
      fields: [
        {
          id: 'legalType',
          label: 'Legal Type',
          children: ({ value, onChange }) => (
            <Select value={(value as string) ?? ''} onValueChange={onChange} disabled={props.disableForm}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Legal Type..." />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_LEGAL_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ),
        },
        {
          id: 'industry',
          label: 'Industry',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Industry"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'dbaName',
          label: 'Doing Business As (DBA)',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="DBA name, if different from the legal name"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'registrationId',
          label: 'Registration ID',
          description: 'International business registration identifier. EIN or Registration ID is required.',
          children: ({ value, onBlur }) => (
            <Grid gap={8}>
              <Input
                className="w-full"
                value={(value as IdentificationDocument)?.number ?? ''}
                onChange={(event) => form.setValue('registrationId.number', event.target.value, { shouldDirty: true })}
                onBlur={onBlur}
                placeholder="Registration Number"
                disabled={props.disableForm}
              />
              <Select
                value={(value as IdentificationDocument)?.countryCode ?? ''}
                onValueChange={(val) => form.setValue('registrationId.countryCode', val, { shouldDirty: true })}
                disabled={props.disableForm}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Country..." />
                </SelectTrigger>
                <SelectContent>
                  {countryOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Grid>
          ),
        },
      ],
    },
    {
      headline: 'Incorporation',
      isCollapsed: true,
      fields: [
        {
          id: 'countryOfIncorporation',
          label: 'Country of Incorporation',
          children: ({ value, onChange }) => (
            <Select value={(value as string) ?? ''} onValueChange={onChange} disabled={props.disableForm}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Country..." />
              </SelectTrigger>
              <SelectContent>
                {countryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ),
        },
        {
          id: 'stateOfIncorporation',
          label: 'State of Incorporation',
          children: ({ value, onChange }) => {
            // Only build the state list once a country is chosen. With no country selected this
            // would otherwise materialize every state/region worldwide (~5k options), which is what
            // made expanding this section slow. Disabled until a country is picked, mirroring how
            // the address state field scopes its options to the selected country.
            const filteredStates = incorporationCountryCode
              ? states.filter((s) => s.country_code === incorporationCountryCode)
              : [];
            return (
              <Select
                value={(value as string) ?? ''}
                onValueChange={onChange}
                disabled={props.disableForm || !incorporationCountryCode}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={incorporationCountryCode ? 'Select State...' : 'Select a country first...'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {filteredStates.map((s) => (
                    <SelectItem key={s.state_code} value={s.state_code}>
                      {s.name} ({s.state_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          },
        },
        {
          id: 'dateOfIncorporation',
          label: 'Date of Incorporation',
          children: ({ value, onChange }) => (
            <DateInput value={(value as string) ?? ''} onChange={onChange} isDisabled={props.disableForm} />
          ),
        },
        {
          id: 'yearOfIncorporation',
          label: 'Year of Incorporation',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="e.g. 2015"
              disabled={props.disableForm}
            />
          ),
        },
      ],
    },
    {
      headline: 'Operations & Activity',
      isCollapsed: true,
      fields: [
        {
          id: 'description',
          label: 'Description',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Description of the business entity"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'purposeOfAccount',
          label: 'Purpose of Account',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Intended use of the account"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'accountUsage',
          label: 'Account Usage',
          description: 'Comma-separated list.',
          children: ({ value, onChange }) => (
            <StringListInput
              value={value as string[] | undefined}
              onChange={onChange}
              placeholder="Possible uses of the accounts"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'paymentVolumes',
          label: 'Payment Volumes',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Expected payment volumes"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'countriesOfOperation',
          label: 'Countries of Operation',
          description: 'Comma-separated ISO country codes, e.g. US, CA, GB.',
          children: ({ value, onChange }) => (
            <StringListInput
              value={value as string[] | undefined}
              onChange={onChange}
              placeholder="US, CA, GB"
              isDisabled={props.disableForm}
            />
          ),
        },
        {
          id: 'sourceOfFunds',
          label: 'Source of Funds',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Primary source of funds"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'expectedUseOfFunds',
          label: 'Expected Use of Funds',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Expected use of funds"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'endCustomerType',
          label: 'End Customer Type',
          children: ({ value, onChange, onBlur }) => (
            <Input
              className="w-full"
              value={(value as string) ?? ''}
              onChange={onChange}
              onBlur={onBlur}
              placeholder="Type of end customer the business serves"
              disabled={props.disableForm}
            />
          ),
        },
        {
          id: 'expectedPaymentRails',
          label: 'Expected Payment Rails',
          children: ({ value, onChange }) => (
            <PaymentRailsInput value={value} onChange={onChange} isDisabled={props.disableForm} />
          ),
        },
        {
          id: 'makesInternationalPayments',
          label: 'Makes International Payments?',
          children: ({ value, onChange }) => (
            <YesNoInput value={value} onChange={onChange} isDisabled={props.disableForm} />
          ),
        },
        {
          id: 'hasVirtualAddress',
          label: 'Has Virtual Address?',
          children: ({ value, onChange }) => (
            <YesNoInput value={value} onChange={onChange} isDisabled={props.disableForm} />
          ),
        },
      ],
    },
    {
      headline: 'Compliance',
      isCollapsed: true,
      fields: [
        {
          id: 'isHighRisk',
          label: 'High Risk?',
          children: ({ value, onChange }) => (
            <YesNoInput value={value} onChange={onChange} isDisabled={props.disableForm} />
          ),
        },
      ],
    },
  ];

  return (
    <>
      <RenderFields sections={businessSections} />
      {/* Rendered with Section (not SectionHeader) so the "Associated Persons" heading matches the
          spacing and divider of the other form sections like Address. */}
      <Section headline="Associated Persons">
        <Flex flexDirection="column" gap={16}>
          {(fields ?? []).map((field, index) => (
            <Card key={field.id} variant="secondary" ref={(el) => (associatedPersonRefs.current[index] = el)}>
              <Flex alignItems="center">
                <SectionHeader text="Associated Person" counter={index + 1} />
                {fields.length > 1 && !props.disableForm && (
                  <Inner pr={16} pl={0} py={0}>
                    <Button variant="danger" size="sm" onClick={() => remove(index)}>
                      Remove
                    </Button>
                  </Inner>
                )}
              </Flex>
              <Inner px={0} pt={0} pb={16}>
                <AssociatedPersonFields index={index} disabled={props.disableForm} />
              </Inner>
            </Card>
          ))}
        </Flex>
        {!props.disableForm && (
          <Button
            type="button"
            onClick={() => {
              append(createDefaultAssociatedPerson());
              setTimeout(() => {
                associatedPersonRefs.current[fields.length]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 0);
            }}
            size="sm"
            variant="muted"
            className="w-full bg-gray-50 hover:bg-gray-100 dark:bg-white/5 dark:hover:bg-white/10"
          >
            <Icon.Plus className="[--icon-size:16px]" />
            Add Associated Person
          </Button>
        )}
      </Section>
    </>
  );
};
